import { Component, ElementRef, computed, forwardRef, inject, input, model, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SelectOption {
  value: string;
  label: string;
}

let nextId = 0;

/**
 * Lista desplegable con el estilo de la tienda: la del <select> nativo la dibuja el
 * sistema operativo y no se puede estilizar. Sirve con [(value)] o con formControlName.
 * Teclado: flechas, Inicio/Fin, Enter/Espacio para elegir y Escape para cerrar.
 */
@Component({
  selector: 'app-select',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SelectComponent), multi: true }],
  host: { class: 'relative block', '(document:click)': 'onDocumentClick($event)' },
  template: `
    <button
      type="button"
      [id]="buttonId()"
      role="combobox"
      aria-haspopup="listbox"
      [attr.aria-expanded]="open()"
      [attr.aria-controls]="listId"
      [attr.aria-label]="ariaLabel() || null"
      [attr.aria-activedescendant]="open() ? listId + '-' + active() : null"
      [disabled]="disabled()"
      (click)="toggle()"
      (keydown)="onKeydown($event)"
      (blur)="onTouched()"
      class="w-full flex items-center justify-between gap-2 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      [class]="
        variant() === 'bare'
          ? 'text-xs font-semibold text-gray-700 hover:text-primary-600 px-3 py-2'
          : 'rounded-lg border bg-white text-sm text-gray-700 px-3 ' + (size() === 'lg' ? 'py-2.5 ' : 'py-2 ') + (open() ? 'border-primary-500 ring-2 ring-primary-500/30' : 'border-gray-300 hover:border-gray-400')
      "
    >
      <span class="truncate">{{ selectedLabel() }}</span>
      <svg class="w-4 h-4 shrink-0 text-gray-400 transition-transform" [class.rotate-180]="open()" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <path d="m6 9 6 6 6-6" />
      </svg>
    </button>
    @if (open()) {
      <ul
        [id]="listId"
        role="listbox"
        [attr.aria-labelledby]="buttonId()"
        class="absolute z-50 left-0 mt-1 min-w-full w-max max-w-[min(20rem,90vw)] max-h-64 overflow-y-auto rounded-xl border border-gray-200 bg-white p-1 shadow-xl"
      >
        @for (opt of options(); track opt.value; let i = $index) {
          <li
            [id]="listId + '-' + i"
            role="option"
            [attr.aria-selected]="opt.value === value()"
            (click)="choose(opt.value)"
            (mouseenter)="active.set(i)"
            class="flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm cursor-pointer"
            [class]="(i === active() ? 'bg-primary-50 ' : '') + (opt.value === value() ? 'text-primary-700 font-semibold' : 'text-gray-700')"
          >
            <span class="truncate">{{ opt.label }}</span>
            @if (opt.value === value()) {
              <svg class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M5 12.5 10 17.5 19 7" /></svg>
            }
          </li>
        }
      </ul>
    }
  `,
})
export class SelectComponent implements ControlValueAccessor {
  private host = inject(ElementRef<HTMLElement>);

  readonly options = input.required<SelectOption[]>();
  readonly value = model('');
  readonly ariaLabel = input('');
  /** id del botón, para enlazarlo con un <label for>. */
  readonly inputId = input('');
  /** 'field': como los inputs del formulario; 'bare': sin borde (barra de búsqueda). */
  readonly variant = input<'field' | 'bare'>('field');
  /** Alto: 'lg' iguala a los inputs py-2.5 de los formularios. */
  readonly size = input<'md' | 'lg'>('md');

  readonly listId = `app-select-${nextId++}`;
  readonly buttonId = computed(() => this.inputId() || this.listId + '-btn');
  readonly open = signal(false);
  readonly active = signal(0);
  readonly disabled = signal(false);
  readonly selectedLabel = computed(() => this.options().find((o) => o.value === this.value())?.label ?? this.options()[0]?.label ?? '');

  private onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  toggle(): void {
    if (this.open()) this.open.set(false);
    else this.openList();
  }

  private openList(): void {
    const index = this.options().findIndex((o) => o.value === this.value());
    this.active.set(Math.max(0, index));
    this.open.set(true);
  }

  choose(value: string): void {
    this.open.set(false);
    if (value === this.value()) return;
    this.value.set(value);
    this.onChange(value);
  }

  onKeydown(event: KeyboardEvent): void {
    const last = this.options().length - 1;
    const keys: Record<string, () => void> = {
      ArrowDown: () => (this.open() ? this.active.update((i) => Math.min(last, i + 1)) : this.openList()),
      ArrowUp: () => (this.open() ? this.active.update((i) => Math.max(0, i - 1)) : this.openList()),
      Home: () => this.open() && this.active.set(0),
      End: () => this.open() && this.active.set(last),
      Enter: () => (this.open() ? this.choose(this.options()[this.active()].value) : this.openList()),
      ' ': () => (this.open() ? this.choose(this.options()[this.active()].value) : this.openList()),
      Escape: () => this.open.set(false),
      Tab: () => this.open.set(false),
    };
    const action = keys[event.key];
    if (!action) return;
    if (event.key !== 'Tab') event.preventDefault();
    action();
  }

  onDocumentClick(event: MouseEvent): void {
    if (this.open() && !this.host.nativeElement.contains(event.target as Node)) this.open.set(false);
  }

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled.set(disabled);
  }
}
